import React, { useEffect, useMemo, useState } from 'react';
import PartnerService from '../../../services/PartnerService';
import ValidationAlerte from './ValidationAlerte';
import RowsPreview from './RowsPreview';

const normalizeValue = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toUpperCase();

const normalizeToken = (value) => normalizeValue(value).replace(/[^A-Z0-9]/g, '');

const normalizeHeader = (value) => normalizeToken(value);

const getRowValue = (row, aliases) => {
  const aliasSet = new Set(aliases.map(normalizeHeader));
  const matchedKey = Object.keys(row || {}).find((key) => aliasSet.has(normalizeHeader(key)));
  return matchedKey ? row[matchedKey] : '';
};

const getPartnerName = (row) => getRowValue(row, [
  'raison_sociale',
  'raison sociale',
  'nom partenaire',
  'nompartenaire',
  'partenaire',
]);

const getPartnerNif = (row) => getRowValue(row, ['nif', 'num nif', 'numero nif']);

const getPartnerStat = (row) => getRowValue(row, ['stat', 'num stat', 'numero stat']);

const getMarketNumber = (row) => getRowValue(row, [
  'numero_marche',
  'numero marche',
  'n marche',
  'marche',
]);

const getAlertKey = (alert) => alert?.id || [
  normalizeToken(alert?.raison_sociale),
  normalizeToken(alert?.nif),
  normalizeToken(alert?.stat),
  normalizeToken(alert?.conflict_stat),
].join('|');

const getRowsFromPreview = (preview) => (preview?.sheets || []).flatMap((sheet) => (
  (sheet.rows || []).map((row, rowIndex) => ({
    row,
    rowIndex,
    sheetName: sheet.sheet_name,
  }))
));

const buildPartnerAlerts = (preview) => {
  const sourceAlerts = Array.isArray(preview?.analysis?.partner_alerts)
    ? preview.analysis.partner_alerts
    : [];

  if (sourceAlerts.length > 0) {
    return sourceAlerts;
  }

  const rows = getRowsFromPreview(preview)
    .map((item) => {
      const raisonSociale = getPartnerName(item.row);
      const nif = getPartnerNif(item.row);
      const stat = getPartnerStat(item.row);

      return {
        ...item,
        raison_sociale: raisonSociale,
        nif,
        stat,
        raison_sociale_key: normalizeToken(raisonSociale),
        nif_key: normalizeToken(nif),
        stat_key: normalizeToken(stat),
      };
    })
    .filter((item) => item.raison_sociale_key || item.nif_key || item.stat_key);

  const alerts = [];

  rows.forEach((current, currentIndex) => {
    rows.slice(currentIndex + 1).forEach((candidate) => {
      const matches = [];

      if (current.raison_sociale_key && current.raison_sociale_key === candidate.raison_sociale_key) {
        matches.push('raison_sociale');
      }

      if (current.nif_key && current.nif_key === candidate.nif_key) {
        matches.push('nif');
      }

      if (current.stat_key && current.stat_key === candidate.stat_key) {
        matches.push('stat');
      }

      if (matches.length === 3) {
        return;
      }

      if (matches.length >= 2 && !matches.includes('stat')) {
        alerts.push({
          id: `${current.sheetName}-${current.rowIndex}-${candidate.sheetName}-${candidate.rowIndex}`,
          raison_sociale: current.raison_sociale || candidate.raison_sociale,
          nif: current.nif || candidate.nif,
          stat: current.stat,
          conflict_stat: candidate.stat,
          matched_fields: matches,
          rows: [
            { sheetName: current.sheetName, rowIndex: current.rowIndex, row: current.row },
            { sheetName: candidate.sheetName, rowIndex: candidate.rowIndex, row: candidate.row },
          ],
        });
      }
    });
  });

  const uniqueAlerts = new Map();
  alerts.forEach((alert) => {
    const key = getAlertKey(alert);
    if (!uniqueAlerts.has(key)) {
      uniqueAlerts.set(key, alert);
    }
  });

  return Array.from(uniqueAlerts.values());
};

const SynchronizeDialog = ({ open, periodIds = [], onClose, onSynchronize }) => {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewErrors, setPreviewErrors] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [activePreviewId, setActivePreviewId] = useState(null);
  const [activeSheetName, setActiveSheetName] = useState('');
  const [highlightMode, setHighlightMode] = useState(null);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertValidations, setAlertValidations] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setPreviews([]);
      setPreviewErrors([]);
      setActivePreviewId(null);
      setActiveSheetName('');
      setHighlightMode(null);
      setAlertDialogOpen(false);
      setAlertValidations({});
      return undefined;
    }

    let cancelled = false;
    setLoadingPreview(true);
    setPreviewErrors([]);
    setPreviews([]);
    setActivePreviewId(null);
    setActiveSheetName('');
    setHighlightMode(null);

    PartnerService.previewPeriods(periodIds).then((result) => {
      if (cancelled) return;
      const nextPreviews = Array.isArray(result?.data?.previews) ? result.data.previews : [];
      const nextErrors = Array.isArray(result?.data?.errors) ? result.data.errors : [];
      setPreviews(nextPreviews);
      setPreviewErrors(nextErrors.length > 0 ? nextErrors : (!result?.status ? [{
        part_period_id: null,
        message: result?.message || 'Aucun aperçu disponible.',
      }] : []));
      setActivePreviewId(nextPreviews[0]?.part_period_id || null);
      setActiveSheetName(nextPreviews[0]?.sheets?.[0]?.sheet_name || '');
    }).catch((error) => {
      if (!cancelled) setPreviewErrors([{ part_period_id: null, message: error?.message || 'Aperçu indisponible.' }]);
    }).finally(() => {
      if (!cancelled) setLoadingPreview(false);
    });

    return () => { cancelled = true; };
  }, [open, periodIds]);

  const activePreview = useMemo(() => (
    previews.find((preview) => Number(preview.part_period_id) === Number(activePreviewId)) || previews[0] || null
  ), [activePreviewId, previews]);
  const activeSheet = useMemo(() => (
    activePreview?.sheets?.find((sheet) => sheet.sheet_name === activeSheetName)
    || activePreview?.sheets?.[0]
    || null
  ), [activePreview, activeSheetName]);
  const activePartnerAlerts = useMemo(() => buildPartnerAlerts(activePreview), [activePreview]);
  const highlightedRows = useMemo(() => {
    const keys = new Set();
    if (!activePreview || !highlightMode) return keys;
    const newPartnerKeys = new Set((activePreview.analysis?.new_partners || []).map((partner) => (
      normalizeToken(partner.name || partner.raison_sociale)
    )));
    const newMarketKeys = new Set((activePreview.analysis?.new_markets || []).map((market) => (
      normalizeToken(market.numero_marche)
    )));
    const alertRowKeys = new Set(activePartnerAlerts.flatMap((alert) => (
      (alert.rows || []).map((row) => row.sheetName + '-' + row.rowIndex)
    )));
    (activePreview.sheets || []).forEach((sheet) => {
      (sheet.rows || []).forEach((row, rowIndex) => {
        const rowKey = sheet.sheet_name + '-' + rowIndex;
        if (highlightMode === 'new_partners' && newPartnerKeys.has(normalizeToken(getPartnerName(row)))) keys.add(rowKey);
        if (highlightMode === 'new_markets' && newMarketKeys.has(normalizeToken(getMarketNumber(row)))) keys.add(rowKey);
        if (highlightMode === 'alerts' && alertRowKeys.has(rowKey)) keys.add(rowKey);
      });
    });
    return keys;
  }, [activePartnerAlerts, activePreview, highlightMode]);
  const previewReady = !loadingPreview
    && previewErrors.length === 0
    && previews.length > 0
    && previews.length === periodIds.length;

  const handleSelectPreview = (id) => {
    const preview = previews.find((item) => Number(item.part_period_id) === Number(id));
    setActivePreviewId(id);
    setActiveSheetName(preview?.sheets?.[0]?.sheet_name || '');
    setHighlightMode(null);
  };

  return (
    <>
      <RowsPreview
        previewOpen={open}
        handleClosePreview={onClose}
        loadingPreview={loadingPreview}
        previewErrors={previewErrors}
        previews={previews}
        activePreview={activePreview}
        activeSheet={activeSheet}
        highlightMode={highlightMode}
        highlightedRows={highlightedRows}
        activePartnerAlerts={activePartnerAlerts}
        handleSelectPreview={handleSelectPreview}
        setActiveSheetName={setActiveSheetName}
        setHighlightMode={setHighlightMode}
        handleOpenAlerts={() => { setHighlightMode('alerts'); setAlertDialogOpen(true); }}
        previewReady={previewReady}
        onSynchronize={onSynchronize}
        onClose={onClose}
        selectedIds={periodIds}
        submitting={submitting}
        setSubmitting={setSubmitting}
      />
      <ValidationAlerte
        open={alertDialogOpen}
        alerts={activePartnerAlerts}
        validations={alertValidations}
        onClose={() => setAlertDialogOpen(false)}
        onSave={(next) => setAlertValidations((current) => ({ ...current, ...next }))}
      />
    </>
  );
};

export default SynchronizeDialog;
